import {
    CreateWorkerData,
    FeWorker,
    FsWorker,
    UpdateWorkerInput,
    UpdateWorkerResult,
    UpdateWorkerStatusResult
} from "./types";
import firebase from "firebase/app";
import _ from "lodash";
import {replaceUndefinedWithDelete} from "../../../utils";

const workerDataConverter: firebase.firestore.FirestoreDataConverter<FeWorker> = {
    fromFirestore(snapshot: firebase.firestore.QueryDocumentSnapshot<FsWorker>): FeWorker {
        const {createdAt, updatedAt, ...docData} = snapshot.data();
        return {
            id: snapshot.id,
            ...docData,
            createdAt: createdAt.toMillis(),
            updatedAt: updatedAt.toMillis()
        }
    },
    toFirestore({id, createdAt, updatedAt, ...docData}: FeWorker): FsWorker {
        return {
            ..._.pickBy(docData, _.identity) as FeWorker,
            createdAt: firebase.firestore.Timestamp.fromMillis(createdAt),
            updatedAt: firebase.firestore.Timestamp.fromMillis(updatedAt),
        };
    }
}

export class WorkersService {
    private static GetCollection() {
        return firebase.firestore()
            .collection('workers')
            .withConverter(workerDataConverter);
    }

    async createWorker(dto: CreateWorkerData): Promise<FeWorker> {
        const result = await firebase.app()
            .functions('europe-west3')
            .httpsCallable('createWorker')(dto);
        return {
            ...result.data as FeWorker,
            createdAt: result.data.createdAt,
            updatedAt: result.data.updatedAt,
        }
    }

    async activateWorker(workerId: string): Promise<UpdateWorkerStatusResult> {
        const result = await firebase.app()
            .functions('europe-west3')
            .httpsCallable('activateWorker')(workerId);
        return {
            ...result.data as UpdateWorkerStatusResult,
            updatedAt: new Date().getTime()
        }
    }

    async deactivateWorker(workerId: string): Promise<UpdateWorkerStatusResult> {
        const result = await firebase.app()
            .functions('europe-west3')
            .httpsCallable('deactivateWorker')(workerId);
        return {
            ...result.data as UpdateWorkerStatusResult,
            updatedAt: new Date().getTime()
        }
    }

    updateWorkerStatus(workerId: string, active: boolean) {
        return active ? this.activateWorker(workerId) : this.deactivateWorker(workerId);
    }

    async updateWorker(id: string, dto: UpdateWorkerInput): Promise<UpdateWorkerResult> {
        await WorkersService.GetCollection().doc(id).update({
            ...replaceUndefinedWithDelete(dto),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return {
            id,
            updatedAt: new Date().getTime(),
            ...dto,
        }
    }

    async listWorkers(): Promise<FeWorker[]> {
        const querySnapshots = await WorkersService.GetCollection().get();
        return querySnapshots.empty
            ? []
            : querySnapshots.docs.map(d => d.data())
    }

    async getWorker(id: string): Promise<FeWorker> {
        const snapshot = await WorkersService.GetCollection().doc(id).get();
        const worker = snapshot.data();
        if (!worker) {
            throw new Error('Worker not found');
        }
        return worker;
    }

    async addWorkerService(workerId: string, serviceId: string) {
        await WorkersService.GetCollection()
            .doc(workerId)
            .update({
                servicesIds: firebase.firestore.FieldValue.arrayUnion(serviceId),
                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
            });

        return {
            workerId,
            serviceId,
            updatedAt: new Date().getTime()
        }
    }

    async removeWorkerService(workerId: string, serviceId: string) {
        await WorkersService.GetCollection()
            .doc(workerId)
            .update({
                servicesIds: firebase.firestore.FieldValue.arrayRemove(serviceId),
                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
            });
        return {
            workerId,
            serviceId,
            updatedAt: new Date().getTime()
        }
    }
}